import { useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiCheck, FiCheckCircle, FiEye, FiEyeOff, FiMail, FiMessageSquare, FiSave, FiSend, FiVideo, FiX, FiZap } from 'react-icons/fi';
import { type NotificationSettings, type OnboardingConfig, type UpdateNotificationSettingsPayload, adminApi } from '../../services/admin';
import styles from './Admin.module.css';
import { AdminLayout } from './AdminLayout';

function StatusBadge({ ok, label }: { ok: boolean; label: string }) {
    return (
        <span className={ok ? styles.badgeSuccess : styles.badgeWarn}>
            {ok ? <FiCheckCircle size={13} /> : <FiAlertTriangle size={13} />}
            {label}
        </span>
    );
}

function SecretInput({ value, onChange, placeholder, disabled }: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    disabled?: boolean;
}) {
    const [visible, setVisible] = useState(false);
    return (
        <div className={styles.settingsSecretRow}>
            <input
                type={visible ? 'text' : 'password'}
                className={styles.settingsEditInput}
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                autoComplete="off"
            />
            <button
                type="button"
                className={styles.settingsSecretToggle}
                onClick={() => setVisible(v => !v)}
                tabIndex={-1}
                aria-label={visible ? 'Ocultar' : 'Mostrar'}
            >
                {visible ? <FiEyeOff size={15} /> : <FiEye size={15} />}
            </button>
        </div>
    );
}

interface TestPanelProps {
    label: string;
    placeholder: string;
    buttonLabel: string;
    disabled: boolean;
    onTest: (value: string) => Promise<string>;
}

function TestPanel({ label, placeholder, buttonLabel, disabled, onTest }: TestPanelProps) {
    const [value, setValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleTest = async () => {
        if (!value.trim()) { inputRef.current?.focus(); return; }
        setLoading(true);
        setResult(null);
        try {
            const msg = await onTest(value.trim());
            setResult({ ok: true, msg });
        } catch (e: any) {
            setResult({ ok: false, msg: e?.message ?? 'Erro ao enviar teste.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.settingsTestPanel}>
            <label className={styles.settingsTestLabel}>{label}</label>
            <div className={styles.settingsTestRow}>
                <input
                    ref={inputRef}
                    className={styles.settingsTestInput}
                    placeholder={placeholder}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleTest()}
                    disabled={disabled || loading}
                />
                <button
                    className={styles.settingsTestBtn}
                    onClick={handleTest}
                    disabled={disabled || loading || !value.trim()}
                >
                    <FiSend size={15} />
                    {loading ? 'Enviando...' : buttonLabel}
                </button>
            </div>
            {result && (
                <div className={result.ok ? styles.settingsTestOk : styles.settingsTestError}>
                    {result.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                    {result.msg}
                </div>
            )}
        </div>
    );
}

interface EmailForm { apiKey: string; secretKey: string; fromEmail: string; fromName: string; }
interface WappForm { token: string; url: string; }
interface WebhookForm { url: string; }

export function AdminSettings() {
    const [settings, setSettings] = useState<NotificationSettings | null>(null);
    const [loading, setLoading] = useState(true);

    const [emailForm, setEmailForm] = useState<EmailForm>({ apiKey: '', secretKey: '', fromEmail: '', fromName: '' });
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailResult, setEmailResult] = useState<{ ok: boolean; msg: string } | null>(null);

    const [wappForm, setWappForm] = useState<WappForm>({ token: '', url: '' });
    const [wappSaving, setWappSaving] = useState(false);
    const [wappResult, setWappResult] = useState<{ ok: boolean; msg: string } | null>(null);

    const [webhookForm, setWebhookForm] = useState<WebhookForm>({ url: '' });
    const [webhookSaving, setWebhookSaving] = useState(false);
    const [webhookResult, setWebhookResult] = useState<{ ok: boolean; msg: string } | null>(null);
    const [webhookTesting, setWebhookTesting] = useState(false);
    const [webhookTestResult, setWebhookTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

    const [wappTemplateTest, setWappTemplateTest] = useState({ to: '', templateName: '', language: 'pt_BR' });
    const [wappTemplateTesting, setWappTemplateTesting] = useState(false);
    const [wappTemplateResult, setWappTemplateResult] = useState<{ ok: boolean; msg: string } | null>(null);

    const [onboardingConfig, setOnboardingConfig] = useState<OnboardingConfig | null>(null);
    const [onboardingVideoUrl, setOnboardingVideoUrl] = useState('');
    const [onboardingSaving, setOnboardingSaving] = useState(false);
    const [onboardingResult, setOnboardingResult] = useState<{ ok: boolean; msg: string } | null>(null);

    useEffect(() => {
        adminApi.getNotificationSettings()
            .then(s => {
                setSettings(s);
                setEmailForm({ apiKey: s.email.apiKey, secretKey: s.email.secretKey, fromEmail: s.email.fromEmail, fromName: s.email.fromName });
                setWappForm({ token: s.whatsapp.token, url: s.whatsapp.url });
                setWebhookForm({ url: s.webhook?.url ?? '' });
            })
            .catch(console.error)
            .finally(() => setLoading(false));

        adminApi.getOnboardingConfig()
            .then(c => {
                setOnboardingConfig(c);
                setOnboardingVideoUrl(c.videoUrl ?? '');
            })
            .catch(console.error);
    }, []);

    const saveEmail = async () => {
        setEmailSaving(true);
        setEmailResult(null);
        try {
            const payload: UpdateNotificationSettingsPayload = {
                mailjetApiKey: emailForm.apiKey,
                mailjetSecretKey: emailForm.secretKey,
                mailjetFromEmail: emailForm.fromEmail,
                mailjetFromName: emailForm.fromName,
            };
            const updated = await adminApi.updateNotificationSettings(payload);
            setSettings(updated);
            setEmailResult({ ok: true, msg: 'Configurações de email salvas.' });
        } catch (e: any) {
            setEmailResult({ ok: false, msg: e?.message ?? 'Erro ao salvar.' });
        } finally {
            setEmailSaving(false);
        }
    };

    const saveWapp = async () => {
        setWappSaving(true);
        setWappResult(null);
        try {
            const payload: UpdateNotificationSettingsPayload = {
                conjochatToken: wappForm.token,
                conjochatUrl: wappForm.url,
            };
            const updated = await adminApi.updateNotificationSettings(payload);
            setSettings(updated);
            setWappResult({ ok: true, msg: 'Configurações de WhatsApp salvas.' });
        } catch (e: any) {
            setWappResult({ ok: false, msg: e?.message ?? 'Erro ao salvar.' });
        } finally {
            setWappSaving(false);
        }
    };

    const saveOnboarding = async () => {
        setOnboardingSaving(true);
        setOnboardingResult(null);
        try {
            const updated = await adminApi.updateOnboardingConfig({ videoUrl: onboardingVideoUrl });
            setOnboardingConfig(updated);
            setOnboardingResult({ ok: true, msg: 'URL do vídeo de onboarding salva.' });
        } catch (e: any) {
            setOnboardingResult({ ok: false, msg: e?.message ?? 'Erro ao salvar.' });
        } finally {
            setOnboardingSaving(false);
        }
    };

    const saveWebhook = async () => {
        setWebhookSaving(true);
        setWebhookResult(null);
        try {
            const updated = await adminApi.updateNotificationSettings({ webhookUrl: webhookForm.url });
            setSettings(updated);
            setWebhookResult({ ok: true, msg: 'URL do webhook salva.' });
        } catch (e: any) {
            setWebhookResult({ ok: false, msg: e?.message ?? 'Erro ao salvar.' });
        } finally {
            setWebhookSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <h2>Configurações da Plataforma</h2>
                    <p>Configurações globais do sistema LTQ</p>
                </div>
            </div>

            <div className={styles.settingsSections}>

                {/* ---- Email (Mailjet) ---- */}
                <div className={styles.settingsCard}>
                    <div className={styles.settingsCardHeader}>
                        <FiMail size={18} />
                        <h3>Email — Mailjet</h3>
                        {!loading && settings && (
                            <StatusBadge
                                ok={settings.email.configured}
                                label={settings.email.configured ? 'Configurado' : 'Não configurado'}
                            />
                        )}
                    </div>

                    <div className={styles.settingsEditForm}>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>API Key</label>
                            <SecretInput
                                value={emailForm.apiKey}
                                onChange={v => setEmailForm(f => ({ ...f, apiKey: v }))}
                                placeholder="Mailjet API Key"
                                disabled={loading || emailSaving}
                            />
                        </div>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>Secret Key</label>
                            <SecretInput
                                value={emailForm.secretKey}
                                onChange={v => setEmailForm(f => ({ ...f, secretKey: v }))}
                                placeholder="Mailjet Secret Key"
                                disabled={loading || emailSaving}
                            />
                        </div>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>Email remetente</label>
                            <input
                                type="email"
                                className={styles.settingsEditInput}
                                value={emailForm.fromEmail}
                                onChange={e => setEmailForm(f => ({ ...f, fromEmail: e.target.value }))}
                                placeholder="noreply@empresa.com.br"
                                disabled={loading || emailSaving}
                            />
                        </div>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>Nome remetente</label>
                            <input
                                type="text"
                                className={styles.settingsEditInput}
                                value={emailForm.fromName}
                                onChange={e => setEmailForm(f => ({ ...f, fromName: e.target.value }))}
                                placeholder="LTQ Consult"
                                disabled={loading || emailSaving}
                            />
                        </div>
                        <div className={styles.settingsEditActions}>
                            <button
                                className={styles.settingsSaveBtn}
                                onClick={saveEmail}
                                disabled={loading || emailSaving}
                            >
                                <FiSave size={14} />
                                {emailSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                            {emailResult && (
                                <span className={emailResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                    {emailResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                    {emailResult.msg}
                                </span>
                            )}
                        </div>
                    </div>

                    <TestPanel
                        label="Enviar email de teste"
                        placeholder="destinatario@email.com"
                        buttonLabel="Enviar teste"
                        disabled={!settings?.email.configured}
                        onTest={async (to) => {
                            const r = await adminApi.testEmail(to);
                            return r.message;
                        }}
                    />
                </div>

                {/* ---- WhatsApp (ConjoChat) ---- */}
                <div className={styles.settingsCard}>
                    <div className={styles.settingsCardHeader}>
                        <FiMessageSquare size={18} />
                        <h3>WhatsApp — ConjoChat</h3>
                        {!loading && settings && (
                            <StatusBadge
                                ok={settings.whatsapp.configured}
                                label={settings.whatsapp.configured ? 'Configurado' : 'Não configurado'}
                            />
                        )}
                    </div>

                    <div className={styles.settingsEditForm}>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>Token de acesso</label>
                            <SecretInput
                                value={wappForm.token}
                                onChange={v => setWappForm(f => ({ ...f, token: v }))}
                                placeholder="Bearer token do ConjoChat"
                                disabled={loading || wappSaving}
                            />
                        </div>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>URL da API</label>
                            <input
                                type="url"
                                className={styles.settingsEditInput}
                                value={wappForm.url}
                                onChange={e => setWappForm(f => ({ ...f, url: e.target.value }))}
                                placeholder="https://api.conjochat.com.br/api/messages/send"
                                disabled={loading || wappSaving}
                            />
                        </div>
                        <div className={styles.settingsEditActions}>
                            <button
                                className={styles.settingsSaveBtn}
                                onClick={saveWapp}
                                disabled={loading || wappSaving}
                            >
                                <FiSave size={14} />
                                {wappSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                            {wappResult && (
                                <span className={wappResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                    {wappResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                    {wappResult.msg}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Teste unificado: livre ou template oficial */}
                    <div className={styles.settingsTestPanel}>
                        <label className={styles.settingsTestLabel}>Enviar WhatsApp de teste</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <input
                                    className={styles.settingsTestInput}
                                    placeholder="+55 11 99999-9999"
                                    value={wappTemplateTest.to}
                                    onChange={e => setWappTemplateTest(f => ({ ...f, to: e.target.value }))}
                                    disabled={!settings?.whatsapp.configured || wappTemplateTesting}
                                />
                                <input
                                    className={styles.settingsTestInput}
                                    placeholder="Template oficial (opcional)"
                                    value={wappTemplateTest.templateName}
                                    onChange={e => setWappTemplateTest(f => ({ ...f, templateName: e.target.value }))}
                                    disabled={!settings?.whatsapp.configured || wappTemplateTesting}
                                />
                                <button
                                    className={styles.settingsTestBtn}
                                    disabled={!settings?.whatsapp.configured || wappTemplateTesting || !wappTemplateTest.to.trim()}
                                    onClick={async () => {
                                        setWappTemplateTesting(true);
                                        setWappTemplateResult(null);
                                        try {
                                            const r = wappTemplateTest.templateName.trim()
                                                ? await adminApi.testWhatsappTemplate(wappTemplateTest.to.trim(), wappTemplateTest.templateName.trim(), [], wappTemplateTest.language || 'pt_BR')
                                                : await adminApi.testWhatsapp(wappTemplateTest.to.trim());
                                            setWappTemplateResult({ ok: true, msg: r.message });
                                        } catch (e: any) {
                                            setWappTemplateResult({ ok: false, msg: e?.message ?? 'Erro ao enviar.' });
                                        } finally {
                                            setWappTemplateTesting(false);
                                        }
                                    }}
                                >
                                    <FiSend size={15} />
                                    {wappTemplateTesting ? 'Enviando...' : 'Enviar teste'}
                                </button>
                            </div>
                            {wappTemplateResult && (
                                <div className={wappTemplateResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                    {wappTemplateResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                    {wappTemplateResult.msg}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ---- Webhook ---- */}
                <div className={styles.settingsCard}>
                    <div className={styles.settingsCardHeader}>
                        <FiZap size={18} />
                        <h3>Webhook</h3>
                        {!loading && settings && (
                            <StatusBadge
                                ok={!!settings.webhook?.configured}
                                label={settings.webhook?.configured ? 'Configurado' : 'Não configurado'}
                            />
                        )}
                    </div>

                    <div className={styles.settingsEditForm}>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>URL do endpoint</label>
                            <input
                                type="url"
                                className={styles.settingsEditInput}
                                value={webhookForm.url}
                                onChange={e => setWebhookForm(f => ({ ...f, url: e.target.value }))}
                                placeholder="https://hooks.exemplo.com/notify"
                                disabled={loading || webhookSaving}
                            />
                        </div>
                        <div className={styles.settingsEditActions}>
                            <button
                                className={styles.settingsSaveBtn}
                                onClick={saveWebhook}
                                disabled={loading || webhookSaving}
                            >
                                <FiSave size={14} />
                                {webhookSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                            {webhookResult && (
                                <span className={webhookResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                    {webhookResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                    {webhookResult.msg}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className={styles.settingsTestPanel}>
                        <label className={styles.settingsTestLabel}>Payload enviado</label>
                        <pre className={styles.settingsWebhookPayload}>{`{
  "event": "USER_REGISTERED",
  "timestamp": "2026-05-27T12:00:00.000Z",
  "userId": "abc-123",
  "data": {
    "userName": "João Silva",
    "userEmail": "joao@empresa.com",
    "planName": "Pro"
  }
}`}</pre>
                    </div>

                    <div className={styles.settingsTestPanel}>
                        <label className={styles.settingsTestLabel}>Enviar webhook de teste</label>
                        <div className={styles.settingsTestRow}>
                            <button
                                className={styles.settingsTestBtn}
                                disabled={!settings?.webhook?.configured || webhookTesting}
                                onClick={async () => {
                                    setWebhookTesting(true);
                                    setWebhookTestResult(null);
                                    try {
                                        const r = await adminApi.testWebhook();
                                        setWebhookTestResult({ ok: true, msg: r.message });
                                    } catch (e: any) {
                                        setWebhookTestResult({ ok: false, msg: e?.message ?? 'Erro ao enviar.' });
                                    } finally {
                                        setWebhookTesting(false);
                                    }
                                }}
                            >
                                <FiSend size={15} />
                                {webhookTesting ? 'Enviando...' : 'Enviar teste'}
                            </button>
                        </div>
                        {webhookTestResult && (
                            <div className={webhookTestResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                {webhookTestResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                {webhookTestResult.msg}
                            </div>
                        )}
                    </div>
                </div>

                {/* ---- Onboarding ---- */}                <div className={styles.settingsCard}>
                    <div className={styles.settingsCardHeader}>
                        <FiVideo size={18} />
                        <h3>Onboarding — Vídeo de boas-vindas</h3>
                        {onboardingConfig && (
                            <StatusBadge
                                ok={!!onboardingConfig.videoUrl}
                                label={onboardingConfig.videoUrl ? 'Configurado' : 'Não configurado'}
                            />
                        )}
                    </div>

                    <div className={styles.settingsEditForm}>
                        <div className={styles.settingsEditGroup}>
                            <label className={styles.settingsEditLabel}>URL do vídeo no YouTube</label>
                            <input
                                type="url"
                                className={styles.settingsEditInput}
                                value={onboardingVideoUrl}
                                onChange={e => setOnboardingVideoUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                disabled={onboardingSaving}
                            />
                        </div>
                        <div className={styles.settingsEditActions}>
                            <button
                                className={styles.settingsSaveBtn}
                                onClick={saveOnboarding}
                                disabled={onboardingSaving}
                            >
                                <FiSave size={14} />
                                {onboardingSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                            {onboardingResult && (
                                <span className={onboardingResult.ok ? styles.settingsTestOk : styles.settingsTestError}>
                                    {onboardingResult.ok ? <FiCheck size={14} /> : <FiX size={14} />}
                                    {onboardingResult.msg}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

            </div>
        </AdminLayout>
    );
}

